import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Dialog, IconButton, Slider, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { DOCUMENT } from '../constants/documentTheme';
import { mediaUrl } from '../utils/memoriesHost';
import {
  createSlideshowAmbient,
  SLIDESHOW_INTERVAL_DEFAULT,
  SLIDESHOW_INTERVAL_MAX,
  SLIDESHOW_INTERVAL_MIN,
  SLIDESHOW_VOLUME_DEFAULT,
} from '../utils/memoriesSlideshowAudio';

const sliderSx = {
  color: DOCUMENT.oxblood,
  '& .MuiSlider-thumb': {
    borderRadius: 0,
    width: 14,
    height: 14,
    backgroundColor: DOCUMENT.ivory,
    border: `1px solid ${DOCUMENT.oxblood}`,
  },
  '& .MuiSlider-rail': { opacity: 0.35 },
  '& .MuiSlider-valueLabel': {
    backgroundColor: DOCUMENT.oxblood,
    borderRadius: 0,
    fontFamily: DOCUMENT.sans,
  },
};

const controlBtnSx = {
  color: DOCUMENT.ivory,
  border: `1px solid rgba(245, 240, 232, 0.22)`,
  borderRadius: 0,
  backgroundColor: 'rgba(26, 18, 16, 0.55)',
  '&:hover': { backgroundColor: 'rgba(26, 18, 16, 0.8)' },
  '&.Mui-disabled': { color: 'rgba(245, 240, 232, 0.28)' },
};

/**
 * Fullscreen album slideshow: manual prev/next, auto-advance with interval slider,
 * ambient music with mute.
 */
export default function MemoriesSlideshow({
  open,
  frames = [],
  startIndex = 0,
  musicSrc = null,
  onClose,
}) {
  const { t } = useTranslation();
  const list = Array.isArray(frames) ? frames : [];
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [intervalSec, setIntervalSec] = useState(SLIDESHOW_INTERVAL_DEFAULT);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(SLIDESHOW_VOLUME_DEFAULT);
  const audioRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const safe = Math.max(0, Math.min(startIndex, Math.max(list.length - 1, 0)));
    setIndex(safe);
    setPlaying(true);
    setMuted(true);
    setVolume(SLIDESHOW_VOLUME_DEFAULT);

    const ambient = createSlideshowAmbient({
      muted: true,
      volume: SLIDESHOW_VOLUME_DEFAULT,
      src: musicSrc || undefined,
    });
    audioRef.current = ambient;
    ambient.start();

    return () => {
      ambient.stop();
      audioRef.current = null;
    };
    // Re-init when opening or when album soundtrack changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, musicSrc]);

  useEffect(() => {
    audioRef.current?.setMuted(muted);
  }, [muted]);

  useEffect(() => {
    audioRef.current?.setVolume(volume);
  }, [volume]);

  const onVolumeChange = (_, value) => {
    const next = Number(value) / 100;
    setVolume(next);
    if (next > 0) setMuted(false);
  };

  const go = useCallback((delta) => {
    if (list.length === 0) return;
    setIndex((current) => (current + delta + list.length) % list.length);
  }, [list.length]);

  useEffect(() => {
    if (!open || !playing || list.length < 2) return undefined;
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % list.length);
    }, Math.max(SLIDESHOW_INTERVAL_MIN, intervalSec) * 1000);
    return () => clearInterval(timer);
  }, [open, playing, intervalSec, list.length, index]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
        return;
      }
      if (event.key === 'ArrowRight' || event.key === ' ') {
        event.preventDefault();
        if (event.key === ' ') setPlaying((p) => !p);
        else go(1);
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      }
      if (event.key === 'm' || event.key === 'M') {
        setMuted((m) => !m);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, go, onClose]);

  const frame = list[index] || null;

  const onTouchStart = (event) => {
    touchStartX.current = event.changedTouches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (event) => {
    const start = touchStartX.current;
    const end = event.changedTouches?.[0]?.clientX ?? null;
    touchStartX.current = null;
    if (start == null || end == null) return;
    const delta = end - start;
    if (Math.abs(delta) < 48) return;
    go(delta < 0 ? 1 : -1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      data-testid="memories-slideshow"
      PaperProps={{
        sx: {
          backgroundColor: '#0E0A09',
          borderRadius: 0,
          m: 0,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          color: DOCUMENT.ivory,
          fontFamily: DOCUMENT.sans,
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: { xs: 1.5, sm: 2 },
            py: 1.25,
            background: 'linear-gradient(180deg, rgba(14,10,9,0.85) 0%, rgba(14,10,9,0) 100%)',
          }}
        >
          <Typography
            sx={{
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              fontSize: 11,
              color: 'rgba(245,240,232,0.72)',
            }}
          >
            {t('memories.slideshowTitle')}
            {list.length > 0 ? ` · ${index + 1} / ${list.length}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: { xs: 112, sm: 160 },
                px: 0.5,
              }}
            >
              <IconButton
                aria-label={muted || volume <= 0 ? t('memories.slideshowUnmute') : t('memories.slideshowMute')}
                onClick={() => {
                  if (muted || volume <= 0) {
                    if (volume <= 0) setVolume(SLIDESHOW_VOLUME_DEFAULT);
                    setMuted(false);
                  } else {
                    setMuted(true);
                  }
                }}
                data-testid="memories-slideshow-mute"
                sx={controlBtnSx}
                size="small"
              >
                {muted || volume <= 0
                  ? <VolumeOffIcon fontSize="small" />
                  : <VolumeUpIcon fontSize="small" />}
              </IconButton>
              <Slider
                value={Math.round(volume * 100)}
                min={0}
                max={100}
                step={1}
                onChange={onVolumeChange}
                aria-label={t('memories.slideshowVolume')}
                data-testid="memories-slideshow-volume"
                sx={{ ...sliderSx, width: { xs: 72, sm: 110 } }}
              />
            </Box>
            <IconButton
              aria-label={t('common.close')}
              onClick={onClose}
              data-testid="memories-slideshow-close"
              sx={controlBtnSx}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            px: { xs: 1, sm: 6 },
            pt: 7,
            pb: 14,
            minHeight: 0,
          }}
        >
          <IconButton
            aria-label={t('memories.slideshowPrev')}
            onClick={() => go(-1)}
            disabled={list.length < 2}
            data-testid="memories-slideshow-prev"
            sx={{
              ...controlBtnSx,
              position: 'absolute',
              left: { xs: 4, sm: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {frame ? (
            <Box
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
              }}
              data-testid="memories-slideshow-frame"
            >
              <Box
                component="img"
                src={mediaUrl(frame.view_url || frame.thumb_url)}
                alt={frame.caption || ''}
                sx={{
                  maxWidth: '100%',
                  maxHeight: { xs: '58vh', sm: '68vh' },
                  objectFit: 'contain',
                  display: 'block',
                  boxShadow: '0 18px 48px rgba(0,0,0,0.45)',
                }}
              />
              {(frame.caption || frame.uploader_display_name) && (
                <Box sx={{ textAlign: 'center', maxWidth: 640, px: 2 }}>
                  {frame.caption && (
                    <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 18, sm: 22 }, color: DOCUMENT.ivory }}>
                      {frame.caption}
                    </Typography>
                  )}
                  {frame.uploader_display_name && (
                    <Typography sx={{ fontSize: 12, color: 'rgba(245,240,232,0.55)', mt: 0.5, letterSpacing: '0.08em' }}>
                      {frame.uploader_display_name}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Typography sx={{ color: 'rgba(245,240,232,0.5)' }}>{t('memories.slideshowEmpty')}</Typography>
          )}

          <IconButton
            aria-label={t('memories.slideshowNext')}
            onClick={() => go(1)}
            disabled={list.length < 2}
            data-testid="memories-slideshow-next"
            sx={{
              ...controlBtnSx,
              position: 'absolute',
              right: { xs: 4, sm: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            px: { xs: 2, sm: 3 },
            py: 2,
            background: 'linear-gradient(0deg, rgba(14,10,9,0.92) 0%, rgba(14,10,9,0.55) 70%, rgba(14,10,9,0) 100%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <IconButton
              aria-label={t('memories.slideshowPrev')}
              onClick={() => go(-1)}
              disabled={list.length < 2}
              sx={{ ...controlBtnSx, display: { xs: 'inline-flex', sm: 'none' } }}
              size="small"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={playing ? t('memories.slideshowPause') : t('memories.slideshowPlay')}
              onClick={() => setPlaying((p) => !p)}
              data-testid="memories-slideshow-play"
              sx={controlBtnSx}
            >
              {playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton
              aria-label={t('memories.slideshowNext')}
              onClick={() => go(1)}
              disabled={list.length < 2}
              sx={{ ...controlBtnSx, display: { xs: 'inline-flex', sm: 'none' } }}
              size="small"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              maxWidth: 420,
              width: '100%',
              mx: 'auto',
            }}
          >
            <Typography
              sx={{
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontSize: 10,
                color: 'rgba(245,240,232,0.55)',
                whiteSpace: 'nowrap',
                minWidth: 72,
              }}
            >
              {t('memories.slideshowTimer')}
            </Typography>
            <Slider
              value={intervalSec}
              min={SLIDESHOW_INTERVAL_MIN}
              max={SLIDESHOW_INTERVAL_MAX}
              step={1}
              onChange={(_, value) => setIntervalSec(Number(value))}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => t('memories.slideshowSeconds', { count: v })}
              aria-label={t('memories.slideshowTimer')}
              data-testid="memories-slideshow-timer"
              sx={sliderSx}
            />
            <Typography
              sx={{
                fontSize: 12,
                color: 'rgba(245,240,232,0.7)',
                minWidth: 40,
                textAlign: 'right',
              }}
              data-testid="memories-slideshow-timer-value"
            >
              {t('memories.slideshowSeconds', { count: intervalSec })}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
